import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectOptionType } from '../../../../../../components/dialog/helper';
import { DayOfWeekInternal, HolidaysHandling } from '../../../../../../data-access/gql-types/graphql';
import { sortedDays } from '../../../../../../utils/helpers/sorted-days';
import { RepeatType } from '../../condition-types/time-condition/enums/repeat-type.enum';
import { TimeEndType } from '../../condition-types/time-condition/enums/time-end-type.enum';
import { useTriggerFormContext } from '../../context/provider';

export const useOptions = () => {
  const { t } = useTranslation('action');
  const { t: tc } = useTranslation('common');
  const { priceConditionConfig } = useTriggerFormContext();

  const shortDayOfWeek = (day: DayOfWeekInternal) => t(`trigger.shortenedDaysOfWeek.${day.toLowerCase()}`);

  const timeEndOptions: SelectOptionType<TimeEndType>[] = [
    { value: TimeEndType.NEVER, label: t('trigger.timeEndOptions.never') },
    { value: TimeEndType.DATE, label: t('trigger.timeEndOptions.date') },
    { value: TimeEndType.COUNT, label: t('trigger.timeEndOptions.count') },
  ];

  const selectedDaysLabel = useMemo(() => {
    let days = [...priceConditionConfig.selectedDays]
      .sort((a, b) => sortedDays[a] - sortedDays[b])
      .map((day) => shortDayOfWeek(day))
      .join(', ');

    if (priceConditionConfig.holidaysHandling === HolidaysHandling.Perform) {
      days = `${days} ${tc('and')} ${t('trigger.conditions.time.holidays')}`;
    }

    return priceConditionConfig.selectedDays.length ? `: ${days}` : '';
  }, [priceConditionConfig.selectedDays]);

  const repeatOptions: SelectOptionType<RepeatType>[] = [
    { value: RepeatType.DAILY, label: t('trigger.conditions.time.repeat.daily') },
    {
      value: RepeatType.SELECTED,
      label: `${t('trigger.conditions.time.repeat.selected')}${selectedDaysLabel}`,
    },
  ];

  const [repeatSelected, setRepeatSelected] = useState(
    repeatOptions.find((option) => option.value === priceConditionConfig.repeatType) ?? repeatOptions[1],
  );
  const [timeEndType, setTimeEndType] = useState(
    timeEndOptions.find((option) => option.value === priceConditionConfig.timeEndType) ?? timeEndOptions[0],
  );

  return {
    timeEndOptions,
    repeatOptions,
    repeatSelected,
    setRepeatSelected,
    timeEndType,
    setTimeEndType,
    selectedDaysLabel,
  };
};
